import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CreditCard, CheckCircle, XCircle, Eye, EyeSlash, ArrowSquareOut, DeviceMobile, ArrowsClockwise, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";
import { APP_VERSION, clearWebAppCaches } from "@/lib/appVersion";

const Settings = () => {
  const [s, setS] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [mpTesting, setMpTesting] = useState(false);
  const [mpResult, setMpResult] = useState(null);
  const [ver, setVer] = useState(null);
  const [pubVer, setPubVer] = useState(null);
  const [verSaving, setVerSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => toast.error("Erro"));
    api.get("/public/app-version")
      .then(({ data }) => { setVer(data); setPubVer(data); })
      .catch(() => {});
  }, []);

  if (!s) return <div className="text-zinc-500">Carregando...</div>;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        whatsapp_number: s.whatsapp_number,
        whatsapp_message: s.whatsapp_message,
        company_name: s.company_name,
        loyalty_thresholds_months: {
          bronze: Number(s.loyalty_thresholds_months.bronze),
          silver: Number(s.loyalty_thresholds_months.silver),
          gold: Number(s.loyalty_thresholds_months.gold),
          king: Number(s.loyalty_thresholds_months.king),
        },
        loyalty_discounts: {
          bronze: Number(s.loyalty_discounts.bronze),
          silver: Number(s.loyalty_discounts.silver),
          gold: Number(s.loyalty_discounts.gold),
          king: Number(s.loyalty_discounts.king),
        },
        mp_access_token: s.mp_access_token || "",
        mp_public_key: s.mp_public_key || "",
        mp_enabled: !!s.mp_enabled,
        mp_webhook_secret: s.mp_webhook_secret || "",
      });
      toast.success("Configurações salvas");
    } catch { toast.error("Erro ao salvar"); }
  };

  const testMp = async () => {
    setMpTesting(true); setMpResult(null);
    try {
      // ensure latest token is saved
      await api.put("/admin/settings", { mp_access_token: s.mp_access_token || "" });
      const { data } = await api.post("/admin/settings/mp-test");
      setMpResult({ ok: true, ...data });
      toast.success(`Token válido — ${data.nickname || data.email || data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro";
      setMpResult({ ok: false, msg });
      toast.error(`Falha: ${msg}`);
    } finally { setMpTesting(false); }
  };

  const saveVersion = async () => {
    if (!ver?.version) { toast.error("Informe o número da versão"); return; }
    setVerSaving(true);
    try {
      const { data } = await api.put("/admin/app-version", {
        version: ver.version,
        android_apk_url: ver.android_apk_url || null,
        ios_url: ver.ios_url || null,
        web_url: ver.web_url || null,
        message: ver.message || null,
        mandatory: !!ver.mandatory,
        min_supported_version: ver.min_supported_version || null,
      });
      setVer(data); setPubVer(data);
      toast.success("Versão publicada com sucesso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar versão");
    } finally { setVerSaving(false); }
  };

  const handleClearCaches = async () => {
    await clearWebAppCaches();
    toast.success("Caches limpas — recarregando...");
    setTimeout(() => window.location.reload(), 800);
  };

  const F = ({ label, children, hint }) => (
    <div>
      <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1.5">{hint}</div>}
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Configurações</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Sistema</h1>
      </div>

      <Card className="border-zinc-200 p-6 card-soft space-y-5">
        <div className="text-sm font-display font-semibold">Geral</div>
        <F label="Nome da empresa">
          <Input value={s.company_name || ""} onChange={(e) => setS({ ...s, company_name: e.target.value })} data-testid="settings-company-name"/>
        </F>
        <F label="WhatsApp (somente números, com DDI 55)">
          <Input value={s.whatsapp_number || ""} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} placeholder="5532999103556" data-testid="settings-whatsapp-number"/>
        </F>
        <F label="Mensagem padrão WhatsApp">
          <Textarea rows={3} value={s.whatsapp_message || ""} onChange={(e) => setS({ ...s, whatsapp_message: e.target.value })} data-testid="settings-whatsapp-message"/>
        </F>
      </Card>

      <Card className="border-zinc-200 p-6 card-soft space-y-5 mt-4" data-testid="mp-settings-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center"><CreditCard size={18} weight="duotone"/></div>
            <div>
              <div className="text-sm font-display font-semibold">Mercado Pago</div>
              <div className="text-xs text-zinc-500">Pix e cartão automáticos para seus clientes</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Ativo</span>
            <Switch checked={!!s.mp_enabled} onCheckedChange={(v) => setS({ ...s, mp_enabled: v })} data-testid="mp-toggle"/>
          </div>
        </div>

        <F
          label="Access Token (privado)"
          hint={<>Obtenha em <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">painel de credenciais do Mercado Pago <ArrowSquareOut size={12}/></a></>}
        >
          <div className="relative">
            <Input
              type={showToken ? "text" : "password"}
              value={s.mp_access_token || ""}
              onChange={(e) => setS({ ...s, mp_access_token: e.target.value })}
              placeholder="APP_USR-xxxxxxxx-xxxxxx-xxxxxxxxxxxxxxxxxxxxxxxx-xxxxxxxxx"
              className="font-mono pr-10"
              data-testid="mp-access-token"
              autoComplete="off"
            />
            <button type="button" onClick={() => setShowToken((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800">
              {showToken ? <EyeSlash size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </F>

        <F label="Public Key (frontend Checkout Pro / Bricks)">
          <Input
            value={s.mp_public_key || ""}
            onChange={(e) => setS({ ...s, mp_public_key: e.target.value })}
            placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="font-mono"
            data-testid="mp-public-key"
            autoComplete="off"
          />
        </F>

        <F label="Webhook secret (opcional, validação do payload)">
          <div className="relative">
            <Input
              type={showSecret ? "text" : "password"}
              value={s.mp_webhook_secret || ""}
              onChange={(e) => setS({ ...s, mp_webhook_secret: e.target.value })}
              className="font-mono pr-10"
              data-testid="mp-webhook-secret"
              autoComplete="off"
            />
            <button type="button" onClick={() => setShowSecret((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800">
              {showSecret ? <EyeSlash size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </F>

        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={testMp} disabled={mpTesting || !s.mp_access_token} variant="outline" data-testid="mp-test-btn">
            {mpTesting ? "Testando..." : "Testar conexão"}
          </Button>
          {mpResult && (
            <div className={`flex items-center gap-2 text-sm ${mpResult.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="mp-test-result">
              {mpResult.ok
                ? <><CheckCircle weight="fill" size={18}/> Conectado · {mpResult.nickname || mpResult.email || `ID ${mpResult.id}`}</>
                : <><XCircle weight="fill" size={18}/> {mpResult.msg}</>
              }
            </div>
          )}
        </div>

        <div className="text-xs text-zinc-500 border-t border-zinc-100 pt-4">
          O Access Token é armazenado de forma segura no banco e usado apenas no backend para criar cobranças e validar webhooks. Nunca é exposto para o cliente.
        </div>
      </Card>

      <Card className="border-zinc-200 p-6 card-soft space-y-5 mt-4">
        <div className="text-sm font-display font-semibold">Fidelidade — meses para cada nível</div>
        <div className="grid sm:grid-cols-4 gap-3">
          {["bronze", "silver", "gold", "king"].map((t) => (
            <F key={t} label={t.toUpperCase()}>
              <Input type="number" min="0" value={s.loyalty_thresholds_months[t]} onChange={(e) => setS({ ...s, loyalty_thresholds_months: { ...s.loyalty_thresholds_months, [t]: e.target.value } })} data-testid={`settings-threshold-${t}`}/>
            </F>
          ))}
        </div>
        <div className="text-xs text-zinc-500">Quantos meses como cliente para atingir cada nível.</div>
      </Card>

      <Card className="border-zinc-200 p-6 card-soft space-y-5 mt-4">
        <div className="text-sm font-display font-semibold">Fidelidade — desconto por nível (%)</div>
        <div className="grid sm:grid-cols-4 gap-3">
          {["bronze", "silver", "gold", "king"].map((t) => (
            <F key={t} label={t.toUpperCase()}>
              <Input type="number" min="0" max="100" value={s.loyalty_discounts[t]} onChange={(e) => setS({ ...s, loyalty_discounts: { ...s.loyalty_discounts, [t]: e.target.value } })} data-testid={`settings-discount-${t}`}/>
            </F>
          ))}
        </div>
      </Card>

      <Card className="border-zinc-200 p-6 card-soft space-y-5 mt-4" data-testid="app-version-card">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center"><DeviceMobile size={18} weight="duotone"/></div>
          <div className="flex-1">
            <div className="text-sm font-display font-semibold">Versão do app</div>
            <div className="text-xs text-zinc-500">Defina a versão mais recente do app. Clientes em versões antigas verão um aviso de atualização.</div>
          </div>
          <span className="text-[10px] tracking-widest uppercase font-semibold bg-zinc-100 border border-zinc-200 text-zinc-600 px-2 py-1 rounded-full">
            Build atual: {APP_VERSION}
          </span>
        </div>

        {ver && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Versão mais recente publicada (semver)" hint="Ex: 1.0.4 — sem 'v' inicial.">
                <Input
                  value={ver.version || ""}
                  onChange={(e) => setVer({ ...ver, version: e.target.value })}
                  placeholder="1.0.4"
                  className="font-mono"
                  data-testid="ver-version"
                />
              </F>
              <F label="Versão mínima suportada (opcional)" hint="Abaixo disso, atualização vira obrigatória.">
                <Input
                  value={ver.min_supported_version || ""}
                  onChange={(e) => setVer({ ...ver, min_supported_version: e.target.value })}
                  placeholder="1.0.0"
                  className="font-mono"
                  data-testid="ver-min-version"
                />
              </F>
            </div>

            <F label="URL do APK Android" hint="Link direto do arquivo .apk para download. O usuário Android será redirecionado para este link.">
              <Input
                value={ver.android_apk_url || ""}
                onChange={(e) => setVer({ ...ver, android_apk_url: e.target.value })}
                placeholder="https://zeferius.com/downloads/zeferius-pay.apk"
                className="font-mono text-sm"
                data-testid="ver-apk-url"
              />
            </F>
            <F label="URL para iOS / App Store">
              <Input
                value={ver.ios_url || ""}
                onChange={(e) => setVer({ ...ver, ios_url: e.target.value })}
                placeholder="https://pay.zeferius.com"
                className="font-mono text-sm"
                data-testid="ver-ios-url"
              />
            </F>
            <F label="URL Web / PWA" hint="Onde a versão web roda. Usado para recarregar o PWA com cache busting.">
              <Input
                value={ver.web_url || ""}
                onChange={(e) => setVer({ ...ver, web_url: e.target.value })}
                placeholder="https://pay.zeferius.com"
                className="font-mono text-sm"
                data-testid="ver-web-url"
              />
            </F>
            <F label="Mensagem exibida para o cliente">
              <Textarea
                rows={2}
                value={ver.message || ""}
                onChange={(e) => setVer({ ...ver, message: e.target.value })}
                placeholder="Atualizamos o Zeferius Pay com melhorias importantes."
                data-testid="ver-message"
              />
            </F>

            <label className="flex items-start gap-3 p-3 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
              <Switch checked={!!ver.mandatory} onCheckedChange={(v) => setVer({ ...ver, mandatory: v })} data-testid="ver-mandatory"/>
              <div className="flex-1">
                <div className="text-sm font-medium">Atualização obrigatória</div>
                <div className="text-xs text-zinc-500">Se marcado, o cliente só consegue continuar usando depois de atualizar.</div>
              </div>
            </label>

            <div className="flex items-center gap-3 flex-wrap pt-2">
              <Button onClick={saveVersion} disabled={verSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ver-save-btn">
                {verSaving ? "Publicando..." : "Publicar versão"}
              </Button>
              <Button onClick={handleClearCaches} variant="outline" data-testid="ver-clear-cache-btn">
                <Broom size={16} className="mr-1.5"/> Limpar cache e recarregar
              </Button>
              {pubVer?.released_at && (
                <span className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
                  <ArrowsClockwise size={12}/> Última publicação: {new Date(pubVer.released_at).toLocaleString("pt-BR")}
                </span>
              )}
            </div>
          </>
        )}
      </Card>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} data-testid="settings-save-btn" className="bg-blue-600 hover:bg-blue-700 text-white">Salvar configurações</Button>
      </div>
    </div>
  );
};

export default Settings;
